import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave,
    faPlus,
    faFilter,
    faTimes,
    faCalendarAlt,
    faSearch,
    faChevronLeft,
    faChevronRight,
    faFileExport,
    faLock,
    faCheck,
    faHourglass,
    faClock,
    faTrash,
    faSpinner
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import SuccessModal from '../../components/SuccessModal';
import { useAuth } from '../../contexts/AuthContext';

const PaymentBatches = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filters
    const [statusFilter, setStatusFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [perPage, setPerPage] = useState(10);

    // Delete/Archive modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const showDeleteModalDismiss = useModalDismiss(() => setShowDeleteModal(false), { backdropClose: false, enabled: showDeleteModal });
    const [batchToDelete, setBatchToDelete] = useState(null);
    const [deleteReason, setDeleteReason] = useState('');
    const [deleting, setDeleting] = useState(false);

    // Success modal
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Bulk export (resp_admin only)
    const [selectedBatchIds, setSelectedBatchIds] = useState([]);
    const [bulkExporting, setBulkExporting] = useState(false);

    useEffect(() => {
        fetchBatches();
    }, [currentPage, statusFilter, searchTerm, dateFrom, dateTo]);

    const fetchBatches = async () => {
        try {
            setLoading(true);
            const response = await api.get('/payments', {
                params: {
                    page: currentPage,
                    per_page: perPage,
                    status: statusFilter,
                    search: searchTerm,
                    date_from: dateFrom,
                    date_to: dateTo,
                },
            });

            if (response.data.success) {
                setBatches(response.data.data.data);
                setCurrentPage(response.data.data.pagination.current_page);
                setTotalPages(response.data.data.pagination.last_page);
                setTotal(response.data.data.pagination.total);
            }
        } catch (err) {
            console.error('Error fetching payment batches:', err);
            setError('Erreur lors du chargement des lots de paiement');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleClearFilters = () => {
        setStatusFilter('');
        setSearchTerm('');
        setDateFrom('');
        setDateTo('');
        setCurrentPage(1);
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            draft: { icon: faClock, text: 'Brouillon', color: 'bg-gray-100 text-gray-700' },
            validated: { icon: faCheck, text: 'Validé', color: 'bg-blue-100 text-blue-700' },
            locked: { icon: faLock, text: 'Verrouillé', color: 'bg-orange-100 text-orange-700' },
            exported: { icon: faFileExport, text: 'Exporté', color: 'bg-green-100 text-green-700' },
            confirmed: { icon: faCheck, text: 'Confirmé', color: 'bg-green-100 text-green-700' },
        };

        const config = statusConfig[status] || { icon: faClock, text: status, color: 'bg-gray-100 text-gray-700' };

        return (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
                <FontAwesomeIcon icon={config.icon} className="mr-1.5" />
                {config.text}
            </span>
        );
    };


    const handleRowClick = (batchId) => {
        navigate(`/payments/${batchId}`);
    };

    const handleDeleteClick = (batch, event) => {
        event.stopPropagation(); // Prevent row click
        setBatchToDelete(batch);
        setShowDeleteModal(true);
    };

    const isRespAdmin = user?.role === 'resp_admin';
    const exportedBatchIds = batches.filter(b => b.status === 'exported').map(b => b.id);

    const toggleBatchSelection = (batchId, event) => {
        event.stopPropagation();
        setSelectedBatchIds(prev =>
            prev.includes(batchId) ? prev.filter(id => id !== batchId) : [...prev, batchId]
        );
    };

    const toggleSelectAllExported = (event) => {
        event.stopPropagation();
        const allSelected = exportedBatchIds.every(id => selectedBatchIds.includes(id));
        if (allSelected) {
            setSelectedBatchIds(prev => prev.filter(id => !exportedBatchIds.includes(id)));
        } else {
            setSelectedBatchIds(prev => [...new Set([...prev, ...exportedBatchIds])]);
        }
    };

    const handleBulkExport = async () => {
        if (selectedBatchIds.length < 2) {
            setError('Veuillez sélectionner au moins 2 lots exportés pour l\'export groupé.');
            return;
        }
        try {
            setBulkExporting(true);
            setError('');
            const response = await api.post('/payments/bulk-export', {
                batch_ids: selectedBatchIds,
            }, { responseType: 'blob' });

            // Download the ZIP
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `export_groupe_lots_${selectedBatchIds.join('-')}_date_${new Date().toLocaleDateString('fr-FR').replace(/\//g, '-')}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setSelectedBatchIds([]);
            setSuccessMessage('Export groupé téléchargé avec succès');
            setShowSuccessModal(true);
        } catch (err) {
            console.error('Error in bulk export:', err);
            if (err.response?.data instanceof Blob) {
                const text = await err.response.data.text();
                try {
                    const json = JSON.parse(text);
                    setError(json.message || 'Erreur lors de l\'export groupé');
                } catch {
                    setError('Erreur lors de l\'export groupé');
                }
            } else {
                setError(err.response?.data?.message || 'Erreur lors de l\'export groupé');
            }
        } finally {
            setBulkExporting(false);
        }
    };

    const handleDelete = async () => {
        if (!batchToDelete) return;

        try {
            setDeleting(true);
            const response = await api.delete(`/payments/${batchToDelete.id}/archive`, {
                data: { reason: deleteReason || '' }
            });

            if (response.data.success) {
                setSuccessMessage('Lot de paiement archivé avec succès');
                setShowSuccessModal(true);
                setShowDeleteModal(false);
                setBatchToDelete(null);
                setDeleteReason('');
                fetchBatches();
            }
        } catch (err) {
            console.error('Error archiving batch:', err);
            if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError('Erreur lors de l\'archivage du lot');
            }
        } finally {
            setDeleting(false);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setBatchToDelete(null);
        setDeleteReason('');
    };

    const hasActiveFilters = statusFilter || searchTerm || dateFrom || dateTo;

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                                GESTION DES PAIEMENTS
                            </h1>
                            <p className="text-gray-600">Créez, validez et exportez les lots de paiement hebdomadaires</p>
                        </div>
                        {user?.role !== 'resp_admin' && (
                            <button
                                onClick={() => navigate('/payments/create')}
                                className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-3 sm:px-6 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center shadow-lg"
                            >
                                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                Nouveau Lot
                            </button>
                        )}
                    </div>
                </div>

                {/* Bulk Export Bar (resp_admin) */}
                {isRespAdmin && selectedBatchIds.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                        <span className="text-sm text-red-800 font-medium">
                            {selectedBatchIds.length} lot{selectedBatchIds.length > 1 ? 's' : ''} sélectionné{selectedBatchIds.length > 1 ? 's' : ''}
                        </span>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setSelectedBatchIds([])}
                                className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                Désélectionner
                            </button>
                            <button
                                onClick={handleBulkExport}
                                disabled={bulkExporting || selectedBatchIds.length < 2}
                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center disabled:opacity-50"
                            >
                                {bulkExporting ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                        Export en cours...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faFileExport} className="mr-2" />
                                        Export groupé
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6 border border-gray-200">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
                        {/* Search and basic filters */}
                        <div className="flex flex-col sm:flex-row gap-3 flex-1">
                            {/* Search */}
                            <div className="relative flex-1 min-w-0">
                                <FontAwesomeIcon
                                    icon={faSearch}
                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                />
                                <input
                                    type="text"
                                    placeholder="Rechercher par ID ou enquête..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                />
                            </div>

                            {/* Status Filter */}
                            <select
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            >
                                <option value="">Tous les statuts</option>
                                <option value="draft">Brouillon</option>
                                <option value="validated">Validé</option>
                                <option value="locked">Verrouillé</option>
                                <option value="exported">Exporté</option>
                            </select>

                        </div>

                        {/* Filter buttons */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center ${
                                    showFilters || hasActiveFilters
                                        ? 'bg-red-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <FontAwesomeIcon icon={faFilter} className="mr-2" />
                                Filtres
                            </button>
                            {hasActiveFilters && (
                                <button
                                    onClick={handleClearFilters}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors duration-200 flex items-center justify-center"
                                >
                                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                                    Effacer
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Extended Filters */}
                    {showFilters && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-gray-400" />
                                    Date de début
                                </label>
                                <input
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => {
                                        setDateFrom(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-gray-400" />
                                    Date de fin
                                </label>
                                <input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => {
                                        setDateTo(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                        {error}
                    </div>
                )}

                {/* Loading State */}
                {loading ? (
                    <div className="bg-white rounded-lg shadow-sm p-6 sm:p-12 text-center border border-gray-200">
                        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                        <p className="text-gray-600 text-sm sm:text-base">Chargement des lots de paiement...</p>
                    </div>
                ) : batches.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-sm p-6 sm:p-12 text-center border border-gray-200">
                        <FontAwesomeIcon icon={faMoneyBillWave} className="text-4xl sm:text-6xl text-gray-300 mb-4" />
                        <p className="text-gray-600 text-base sm:text-lg">Aucun lot de paiement trouvé</p>
                        <p className="text-gray-500 text-xs sm:text-sm mt-2">Créez un nouveau lot pour commencer</p>
                    </div>
                ) : (
                    <>
                        {/* Table */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {isRespAdmin && exportedBatchIds.length > 0 && (
                                            <th className="px-2 sm:px-3 py-3 text-center w-10">
                                                <input
                                                    type="checkbox"
                                                    checked={exportedBatchIds.length > 0 && exportedBatchIds.every(id => selectedBatchIds.includes(id))}
                                                    onChange={toggleSelectAllExported}
                                                    className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500 cursor-pointer"
                                                    title="Sélectionner tous les lots exportés"
                                                />
                                            </th>
                                        )}
                                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            ID
                                        </th>
                                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Lot de paiement
                                        </th>
                                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Période
                                        </th>
                                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Lignes
                                        </th>
                                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Total Net
                                        </th>
                                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Statut
                                        </th>
                                        {user?.role !== 'resp_admin' && (
                                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {batches.map((batch) => (
                                        <tr
                                            key={batch.id}
                                            onClick={() => handleRowClick(batch.id)}
                                            className="hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                                        >
                                            {isRespAdmin && exportedBatchIds.length > 0 && (
                                                <td className="px-2 sm:px-3 py-4 text-center w-10" onClick={e => e.stopPropagation()}>
                                                    {batch.status === 'exported' ? (
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedBatchIds.includes(batch.id)}
                                                            onChange={(e) => toggleBatchSelection(batch.id, e)}
                                                            className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500 cursor-pointer"
                                                        />
                                                    ) : <span />}
                                                </td>
                                            )}
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm font-medium text-gray-900">#{batch.id}</span>
                                            </td>
                                            <td className="px-3 sm:px-6 py-4">
                                                {batch.creation_mode === 'auto' ? (
                                                    // Auto (weekly REM) batches span many surveys — show a short label.
                                                    <span className="text-sm font-medium text-gray-900">REM</span>
                                                ) : batch.creation_mode === 'express' ? (
                                                    // Express batches are frais de déplacement payments.
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-medium text-gray-900">Frais de Déplacement :</span>
                                                        <span className="text-sm text-gray-900">
                                                            {(batch.surveys && batch.surveys.length > 0)
                                                                ? batch.surveys.map(s => s.title).join(', ')
                                                                : (batch.survey?.title || '-')}
                                                        </span>
                                                    </div>
                                                ) : (batch.surveys && batch.surveys.length > 0) ? (
                                                    <div className="flex flex-col gap-1">
                                                        {batch.surveys.map((survey, idx) => (
                                                            <span key={idx} className="text-sm text-gray-900">
                                                                {survey.title}
                                                            </span>
                                                        ))}
                                                        {batch.surveys.length > 1 && (
                                                            <span className="text-xs text-gray-500 font-medium">
                                                                ({batch.surveys.length} enquêtes)
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-sm text-gray-900">
                                                        {batch.survey?.title || '-'}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm text-gray-900">
                                                    {new Date(batch.period_start).toLocaleDateString('fr-FR')} -{' '}
                                                    {new Date(batch.period_end).toLocaleDateString('fr-FR')}
                                                </span>
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm text-gray-900">
                                                    {batch.lines_count}
                                                    {batch.lines_on_hold_count > 0 && (
                                                        <span className="ml-2 text-xs text-orange-600">
                                                            ({batch.lines_on_hold_count} en attente)
                                                        </span>
                                                    )}
                                                </span>
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                {batch.creation_mode === 'express' && parseFloat(batch.credit_applied || 0) > 0 ? (
                                                    <div>
                                                        <span className="text-sm font-semibold text-purple-700">
                                                            {parseFloat(batch.net_a_decaisser).toFixed(2)} MAD
                                                        </span>
                                                        <div className="text-[11px] text-gray-400 line-through">{parseFloat(batch.totals_net).toFixed(2)} MAD</div>
                                                        <div className="text-[10px] text-gray-400">−{parseFloat(batch.credit_applied).toFixed(2)} caisse</div>
                                                    </div>
                                                ) : (
                                                    <span className="text-sm font-semibold text-gray-900">
                                                        {parseFloat(batch.totals_net).toFixed(2)} MAD
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                <div className="flex flex-col gap-1">
                                                    {getStatusBadge(batch.status)}
                                                    {batch.creation_mode === 'auto' && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                                                            Auto
                                                        </span>
                                                    )}
                                                    {batch.creation_mode === 'express' && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                                            Express
                                                        </span>
                                                    )}
                                                    {batch.creation_mode === 'auto' && batch.section_validation && batch.status === 'draft' && (
                                                        <span className="text-xs text-gray-500">
                                                            {[
                                                                batch.section_validation.cati,
                                                                batch.section_validation.f2f,
                                                                batch.section_validation.controller
                                                            ].filter(s => s).filter(s => s.validated).length}
                                                            /
                                                            {[
                                                                batch.section_validation.cati,
                                                                batch.section_validation.f2f,
                                                                batch.section_validation.controller
                                                            ].filter(s => s).length} sections
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            {user?.role !== 'resp_admin' && (
                                                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                    {batch.status === 'draft' && (
                                                        <button
                                                            onClick={(e) => handleDeleteClick(batch, e)}
                                                            className="inline-flex items-center px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 text-sm"
                                                            title="Archiver ce lot"
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} className="mr-1.5" />
                                                            Archiver
                                                        </button>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 gap-4">
                                <div className="flex items-center text-sm text-gray-700">
                                    <span>
                                        Affichage de {((currentPage - 1) * perPage) + 1} à {Math.min(currentPage * perPage, total)} sur {total} résultats
                                    </span>
                                </div>
                                
                                <div className="flex flex-col sm:flex-row items-center gap-3">
                                    {/* Mobile navigation */}
                                    <div className="flex sm:hidden space-x-2">
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                                        </button>
                                        
                                        <div className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg">
                                            {currentPage} / {totalPages}
                                        </div>
                                        
                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                                        </button>
                                    </div>
                                    
                                    {/* Desktop navigation */}
                                    <div className="hidden sm:flex items-center space-x-2">
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                                        </button>
                                        
                                        <div className="flex space-x-1">
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }

                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => handlePageChange(pageNum)}
                                                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                                            currentPage === pageNum
                                                                ? 'bg-red-600 text-white shadow-md'
                                                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        
                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Delete/Archive Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Confirmer l'archivage
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Êtes-vous sûr de vouloir archiver ce lot de paiement ?
                        </p>
                        <p className="text-sm text-red-600 mb-4">
                            Note: Seuls les lots en statut "Brouillon" peuvent être archivés.
                        </p>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Raison d'archivage (optionnel)
                            </label>
                            <textarea
                                value={deleteReason}
                                onChange={(e) => setDeleteReason(e.target.value)}
                                placeholder="Raison de l'archivage..."
                                rows="4"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            />
                        </div>

                        <div className="flex items-center justify-end space-x-3">
                            <button
                                onClick={handleCancelDelete}
                                disabled={deleting}
                                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200 disabled:opacity-50"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={deleting}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                            >
                                {deleting ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                        Archivage...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faTrash} className="mr-2" />
                                        Archiver
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {showSuccessModal && (
                <SuccessModal
                    message={successMessage}
                    onClose={() => setShowSuccessModal(false)}
                />
            )}
        </div>
    );
};

export default PaymentBatches;

